import { FormGroup } from '@/components/element/Form/FormGroup';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { SelectSingle } from '@/components/element/Form/SelectSingle';
import { useEffect, useState } from 'react';
import { SelectOptionType } from '@/components/element/Form/SelectOptionType';
import { SingleValue } from 'react-select';
import { toastApiError } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useCurrentPurchaseOrder } from '@/providers/PurchaseOrderProvider';
import { useSalesOrders } from '@/hooks/api/1210_sales_orders';
import { useRelatedOrdersInvalidate } from '@/hooks/api/0640_related_orders';
import { useCreateRelatedOrder } from '@/hooks/api/0650_create_related_order';
import { useMeSalesOrders } from '@/hooks/api/2470_me_sales_orders';
import { InputPrice } from '@/components/element/Form/InputPrice';
import { InputRatio } from '@/components/element/Form/InputRatio';
import { SalesOrderOutlineLogic } from '@/hooks/api/models/logics';

type FormInputs = {
  identifier: string;
  team_id: number;
  purchase_order_id: number;
  sales_order_id: number;
  allocation_ratio: number;
};

export const RelatedOrderCreateForm = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me, meTeamMember } = useCurrentTeamMember();
  const { _po_, mePurchaseOrderMember } = useCurrentPurchaseOrder();
  // const { data: salesOrders } = useSalesOrders({ identifier: _w_!.identifier, team_id: _t_!.id, page: 1, per: 1000 });
  const { data: salesOrders } = useMeSalesOrders({ identifier: _w_!.identifier });
  const { invalidate } = useRelatedOrdersInvalidate({ identifier: _w_!.identifier, team_id: _t_!.id, id: _po_!.id });
  const { mutate, data, isPending, error } = useCreateRelatedOrder();
  const [options, setOptions] = useState<SelectOptionType[]>([]);
  const {
    control,
    getValues,
    setValue,
    trigger,
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: _po_!.id,
      allocation_ratio: 0,
    },
  });

  useEffect(() => {
    if (salesOrders)
      setOptions(salesOrders.map((d): SelectOptionType => ({ value: d.id, label: SalesOrderOutlineLogic.toLabel(d) })));
  }, [salesOrders]);

  const onSalesOrderChange = (sv: SingleValue<SelectOptionType>) => {
    if (sv) {
      setValue('sales_order_id', sv.value);
      trigger('sales_order_id');
    } else {
      reset(undefined, { keepDefaultValues: true });
    }
  };

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  useEffect(() => {
    if (data) {
      invalidate();
      reset(undefined, { keepDefaultValues: true });
    }
  }, [data]);

  return (
    <>
      <div className="col-12 col-md-7">
        <FormGroup label="関連するプロジェクト（売上）を選択" required={true} classes={''}>
          <Controller
            control={control}
            name="sales_order_id"
            rules={{ required: true }}
            render={({ field: { ref, ...field } }) => (
              <SelectSingle
                options={options}
                selectedId={getValues('sales_order_id')}
                onChange={onSalesOrderChange}
                error={errors.sales_order_id != null}
              />
            )}
          />
        </FormGroup>
      </div>

      <div className="col-12 col-md-2 px-0">
        <FormGroup label="按分比率（％）" required={true} classes={''}>
          <div className="input-group">
            <Controller
              render={({ field: { onChange, value, ...field } }) => (
                <InputRatio onChange={onChange} value={value} error={errors.allocation_ratio} />
              )}
              control={control}
              name="allocation_ratio"
              rules={{ required: true }}
            />
          </div>
        </FormGroup>
      </div>

      <div className="col-12 col-md-3">
        <div className="form-group">
          <label>&nbsp;</label>
          <button className="btn w-100 btn-icon bg-gradient-dark" onClick={() => handleSubmit(onSubmit)()}>
            <i className="fa fa-plus"></i>追加
          </button>
        </div>
      </div>
    </>
  );
};
